import { selectBonuses } from "@/entities/bonus";
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux";
import { useMemo, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { selectChart, selectChartLoading, selectPair, selectChartInterval, selectChartCount, setChartInterval, setChartCount, fetchPairChart } from "../../model";
import { ru } from "@/shared/config/apexcharts-locale";
import { ApexOptions } from "apexcharts";
import { Loader } from "@/shared/ui/loader";
import ReactSelect from "react-select";
import ArrowDown from '@/shared/assets/images/ArrowDown.svg';

const CHART_INTERVALS = [
    { value: 5, label: '5 сек' },
    { value: 60, label: '1 мин' },
    { value: 300, label: '5 мин' },
    { value: 600, label: '10 мин' },
    { value: 1800, label: '30 мин' },
    { value: 3600, label: '1 час' },
]

const CHART_COUNTS = [
    { value: 30, label: '30' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 200, label: '200' },
]

export const PairChart = () => {
    const dispatch = useAppDispatch()
    const bonuses = useAppSelector(selectBonuses)
    const pair = useAppSelector(selectPair)
    const chart = useAppSelector(selectChart)
    const chartLoading = useAppSelector(selectChartLoading)
    const chartInterval = useAppSelector(selectChartInterval)
    const chartCount = useAppSelector(selectChartCount)
    const tradingBonus = useMemo(() => bonuses.find(bonus => bonus.id === pair.bonus1_id), [bonuses, pair])

    useEffect(() => {
        if (pair.id) {
            dispatch(fetchPairChart())
        }
    }, [chartInterval, chartCount, pair.id])

    const parsed = useMemo(() => chart.map((data) => ({
        x: new Date(data.timestamp),
        y: [data.beg_price, data.max_price, data.min_price, data.end_price]
    })), [chart])

    const series: ApexAxisChartSeries = [{
        data: parsed
    }]

    const options: ApexOptions = {
        chart: {
            defaultLocale: "ru",
            type: 'candlestick',
            height: 350,
            zoom: {
                enabled: false,
            },
            toolbar: {
                show: false,
            },
            locales: [ru]
        },
        grid: {
            show: true,
            borderColor: '#474D57',
        },
        tooltip: {
            custom: function ({ seriesIndex, dataPointIndex, w }) {
                const o = w.globals.seriesCandleO[seriesIndex][dataPointIndex]
                const h = w.globals.seriesCandleH[seriesIndex][dataPointIndex]
                const l = w.globals.seriesCandleL[seriesIndex][dataPointIndex]
                const c = w.globals.seriesCandleC[seriesIndex][dataPointIndex]
                return (
                    `<div class="bg-color-dark !outline-none border-color-primary border text-white p-6 !font-mediumttf flex flex-col gap-3 overflow-hidden !rounded-2xl">
                        <div>
                            Открытие: <span class="value">${o}</span>
                        </div>
                        <div>
                            Максимум: <span class="value">${h}</span>
                        </div>
                        <div>
                            Минимум: <span class="value">${l}</span>
                        </div>
                        <div>
                            Закрытие: <span class="value">${c}</span>
                        </div>
                    </div>
                    `
                )
            }
        },
        plotOptions: {
            candlestick: {
                colors: {
                    upward: '#05CD99BB',
                    downward: '#FF2124FF'
                },
                wick: {
                    useFillColor: true,
                }
            }
        },
        xaxis: {
            type: 'datetime',
            labels: {
                style: {
                    colors: 'rgba(255,255,255,0.7)',
                    fontFamily: 'Manrope',
                    fontWeight: 700,
                    fontSize: '12px'
                }
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: 'rgba(255,255,255,0.7)',
                    fontFamily: 'Manrope',
                    fontWeight: 700,
                    fontSize: '12px'
                }
            },
            opposite: true,
            tooltip: {
                enabled: true
            },
            axisBorder: {
                show: false,
            }
        }
    }

    return (
        <div className={'rounded-2xl screen:rounded-3xl transition-all ease-in bg-color-dark w-full px-6 py-8 screen:px-6 screen:py-8 flex flex-col'}>
            <div className="gap-3 flex">
                <div className="w-[56px] h-[56px] relative shrink-0">
                    <div className="w-full h-full bg-white border border-[#E7EAEE] rounded-full overflow-hidden">
                        <img
                            className="w-full h-full object-contain"
                            src={tradingBonus?.image}
                        />
                    </div>
                    <div className="rounded-full top-0 right-0 z-10 absolute bg-color-primary border border-[#E7EAEE] w-[25px] h-[15px] flex items-center justify-center">
                        <div className="text-white text-[7px] font-bold leading-[7px]">
                            соц
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-4 w-full">
                    <div className={'text-white mt-3 transition-all ease-in'}>
                        <div className='text-[32px] screen:text-[36px] leading-[32px] font-mediumttf screen:leading-[36px] font-semibold'>ЦЕНА BON</div>
                    </div>
                    <div className="flex flex-col gap-3 mt-4">
                        <div className="font-bold text-[20px] text-white/70">
                            BON к RUB: 1 Bon = 1 RUB
                        </div>
                        <div className="flex gap-2">
                            <div className="w-[200px] bg-white/10 rounded-full px-4 py-2">
                                <ReactSelect
                                    value={CHART_INTERVALS.find(option => option.value === chartInterval)}
                                    onChange={(e) => e && dispatch(setChartInterval(e.value))}
                                    options={CHART_INTERVALS}
                                    placeholder="Интервал"
                                    className="w-full"
                                    classNames={{
                                        control: () => '!border-0 !p-0 !bg-transparent !text-white !outline-0 !min-h-[auto] w-full !border-transparent !shadow-none',
                                        valueContainer: () => '!border-0 !outline-0 !p-0',
                                        menu: () => '!left-[0]',
                                        singleValue: () => '!text-white !font-mediumttf !text-sm',
                                        input: () => '!text-white font-mediumttf placeholder:!text-white/70 placeholder:!font-mediumttf !text-sm',
                                        placeholder: () => '!text-white/70 !font-mediumttf !text-sm',
                                        option: () => '!text-sm'
                                    }}
                                    classNamePrefix="react_select_trading"
                                    noOptionsMessage={() => 'Нет опций'}
                                    components={{
                                        DropdownIndicator: () => <ArrowDown height={16} width={16} className="fill-white opacity-70" />,
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                            <div className="w-[150px] bg-white/10 rounded-full px-4 py-2">
                                <ReactSelect
                                    value={CHART_COUNTS.find(option => option.value === chartCount)}
                                    onChange={(e) => e && dispatch(setChartCount(e.value))}
                                    options={CHART_COUNTS}
                                    placeholder="Период"
                                    className="w-full"
                                    classNames={{
                                        control: () => '!border-0 !p-0 !bg-transparent !text-white !outline-0 !min-h-[auto] w-full !border-transparent !shadow-none',
                                        valueContainer: () => '!border-0 !outline-0 !p-0',
                                        menu: () => '!left-[0]',
                                        singleValue: () => '!text-white !font-mediumttf !text-sm',
                                        input: () => '!text-white font-mediumttf placeholder:!text-white/70 placeholder:!font-mediumttf !text-sm',
                                        placeholder: () => '!text-white/70 !font-mediumttf !text-sm',
                                        option: () => '!text-sm'
                                    }}
                                    classNamePrefix="react_select_trading"
                                    noOptionsMessage={() => 'Нет опций'}
                                    components={{
                                        DropdownIndicator: () => <ArrowDown height={16} width={16} className="fill-white opacity-70" />,
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {Boolean(parsed.length) &&
                        <div className="-ml-3">
                            <ReactApexChart options={options} series={series} type="candlestick" height={400} />
                        </div>
                    }
                    {(!Boolean(parsed.length) && chartLoading) &&
                        <div>
                            <Loader />
                        </div>
                    }
                    {(!Boolean(parsed.length) && !chartLoading) &&
                        <div className="!text-white/70 !text-lg">
                            История предложений пуста
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}